import { startCase } from 'lodash-es';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';

import UserGroupFormWithTrigger from '@/component/UserGroup/UserGroupCreateForm';
import { USER_CONST } from '@/constant';
import {
  useUserCountQuery,
  useUserRemoveQuery,
  useUserRoleMapper,
} from '@/hook';
import usePaginationSearchParams from '@/hook/tanstackQuery/usePaginationSearchParams';
import type { VTableColumnFilter } from '@/package';
import {
  CheckBox,
  CheckBoxOutlineBlank,
  Remove,
  VFlex,
  VInput,
  VListItem,
  VPagination,
  VStack,
  VTable,
  useVModal,
} from '@/package';
import { VIconButton } from '@/package/kiska/src/component/UI/VIconButton';
import { useAuthStore } from '@/store';
import type { User, UserGroup, UserRole } from '@/type';
import { convertUserEmailToHexColor } from '@/util';

type UserGroupListProps = {
  groupList: UserGroup[];
  onSelectGroup: (groupID: UserGroup['id']) => void;
  openMemberProfile: () => void;
  selectedGroupID: UserGroup['id'];
  selectedGroupUserList: User[];
  setSelectedGroupUserList: Dispatch<SetStateAction<User[]>>;
  setSelectedUser: (user: User) => void;
  userCount: number;
  userList: User[];
};

const UserGroupList = ({
  groupList,
  onSelectGroup,
  openMemberProfile,
  selectedGroupID,
  selectedGroupUserList,
  setSelectedGroupUserList,
  setSelectedUser,
  userCount,
  userList,
}: UserGroupListProps) => {
  const { mapUserRole } = useUserRoleMapper();
  const { limit, offset, setOffset } = usePaginationSearchParams('user');
  const { data: totalUserCount } = useUserCountQuery();
  const me = useAuthStore(state => state.user);
  const allUserGroup: UserGroup = {
    createdAt: '-',
    deletedAt: '-',
    id: 0,
    name: 'All member',
    updatedAt: '-',
    users: Array.from({ length: userCount }),
  };
  const selectedGroup =
    [allUserGroup, ...(groupList ?? [])].find(
      group => group.id === selectedGroupID,
    ) ?? allUserGroup;
  const { mutate: removeUser } = useUserRemoveQuery();
  const { openDangerConfirm } = useVModal();
  const [filteredUserRoleList, setFilteredUserRoleList] = useState<UserRole[]>(
    [],
  );
  const [searchTerm, setSearchTerm] = useState('');

  const isAllUserRoleSelected =
    filteredUserRoleList.length ===
      Object.values(USER_CONST.USER_ROLE).length ||
    filteredUserRoleList.length === 0;

  const filteredUserList = (() => {
    const roleFilteredUserList = isAllUserRoleSelected
      ? userList
      : userList.filter(user => filteredUserRoleList.includes(user.role));

    const searchTermFilteredUserList = roleFilteredUserList.filter(
      user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return searchTermFilteredUserList;
  })();

  const columnFilter = [
    {
      id: 'role',
      itemList: Object.values(USER_CONST.USER_ROLE)
        .filter(role => role !== USER_CONST.USER_ROLE.ADMIN)
        .map(role => ({
          value: role,
          label: mapUserRole(role),
          isSelected: filteredUserRoleList.includes(role),
        })),
    },
  ];
  const isUserRemovable = !(
    me.role === USER_CONST.USER_ROLE.MANAGER ||
    me.role === USER_CONST.USER_ROLE.WORKER ||
    me.role === USER_CONST.USER_ROLE.GUEST
  );

  const isAllSelected =
    userList.length &&
    userList.every(user =>
      selectedGroupUserList.find(userInGroup => userInGroup.id === user.id),
    );

  const onHeaderCheckboxClick = () => {
    isAllSelected
      ? setSelectedGroupUserList([])
      : setSelectedGroupUserList([...(userList ?? [])]);
  };

  const onUserCheckboxClick = (user: User) => {
    const idx = selectedGroupUserList.findIndex(u => u.id === user.id);
    if (idx === -1) {
      setSelectedGroupUserList([...selectedGroupUserList, user]);
    } else {
      setSelectedGroupUserList(
        selectedGroupUserList.filter((_, i) => i !== idx),
      );
    }
  };

  const onUserRemoveButtonClick = (userID: User['id']) => {
    if (!isUserRemovable) return;
    openDangerConfirm({
      title: 'Remove member',
      size: 'small',
      onConfirm: () => removeUser(userID),
      message: () => (
        <VStack className="" gap={0.25}>
          <div className="text-body-xl text-black">
            Are you sure you want to remove the selected member?
          </div>
          <div className="text-body-lg text-gray-600">
            This member will no longer have access to the workspace and all
            related permissions will be revoked.
            <br /> You can invite them again at any time.
          </div>
        </VStack>
      ),
    });
  };

  const renderedUserList = useMemo(() => {
    return (
      filteredUserList.map(user => {
        const name = user.name || user.email.split('@')[0];
        const email = user.email;
        const renderedUser = {
          ...user,
          id: `${user.id}`,
          checked: !!selectedGroupUserList.find(u => u.id === user.id),
          name: { name, email },
          role: user.role,
          lastWorked: null,
          remove: null,

          user: user,
        };
        return renderedUser;
      }) ?? []
    );
  }, [filteredUserList, selectedGroupUserList]);

  useEffect(() => {
    setSelectedGroupUserList([]);
  }, [selectedGroupID]);

  return (
    <VFlex className="h-full flex-1  py-8" gap={1}>
      <div className="h-full w-[324px] min-w-[324px] overflow-y-auto rounded-md border border-skyBlue-200">
        <VListItem<UserGroup & { len?: number }>
          list={[allUserGroup, ...(groupList ?? [])].map(group => ({
            ...group,
            len: group.users.length,
          }))}
          value={selectedGroup}
          onChange={group => {
            onSelectGroup(group.id);
            setOffset(0);
          }}
        />
      </div>
      <VStack className="h-full w-full">
        <VFlex className="w-full gap-2" justify="between">
          <VFlex items="center" gap={0.5}>
            {selectedGroup.id ? (
              <UserGroupFormWithTrigger
                // eslint-disable-next-line react-hooks/purity
                key={Math.random()}
                editOrCreate="edit"
                userGroupList={groupList ?? []}
                userList={userList ?? []}
                editingGroup={selectedGroup}
              />
            ) : (
              <div className="min-w-1"></div>
            )}

            {!!selectedGroupUserList.length && (
              <div className="text-body-xl text-gray-500">
                {selectedGroupUserList.length} selected
              </div>
            )}
          </VFlex>
          <VInput
            type="search"
            placeholder="Search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </VFlex>
        <VStack className="flex-1 overflow-hidden ">
          <div className="flex-1 overflow-y-auto ">
            <VTable
              data={renderedUserList}
              columnList={['checked', 'name', 'role', 'remove']}
              columnStyleMap={{
                checked: { className: isUserRemovable ? 'w-9' : 'hidden' },
                name: { className: 'w-full' },
                role: { className: 'w-[174px]' },
                lastWorked: { className: 'w-[174px]' },
                remove: { className: isUserRemovable ? 'w-[80px]' : 'hidden' },
              }}
              onRowClick={e => {
                setSelectedUser(e._target.row.user);
                openMemberProfile();
              }}
              columnRenderer={{
                checked: () => {
                  const Icon = isAllSelected ? CheckBox : CheckBoxOutlineBlank;
                  return (
                    <Icon
                      className="h-5 w-5"
                      role="button"
                      onClick={onHeaderCheckboxClick}
                    />
                  );
                },
                name: startCase,
                remove: startCase,
                role: startCase,
                lastWorked: startCase,
              }}
              cellRenderer={{
                checked: (value, _, row) => {
                  const Icon = value ? CheckBox : CheckBoxOutlineBlank;

                  return (
                    <Icon
                      className="h-5 w-5"
                      role="button"
                      onClick={e => {
                        e.stopPropagation();
                        onUserCheckboxClick(row.user);
                      }}
                    />
                  );
                },
                name: (value, _, row) => (
                  <VFlex gap={0.5} items="center" role="button">
                    <VFlex
                      justify="center"
                      items="center"
                      className="h-5 w-5 rounded-full"
                      style={{
                        backgroundColor: convertUserEmailToHexColor(
                          row.user.email,
                        ).backgroundColor,
                        color: convertUserEmailToHexColor(row.user.email).color,
                      }}
                    >
                      {value.name[0]}
                    </VFlex>
                    <div className="text-body-xl text-black" role="button">
                      {value.name}
                    </div>
                    <div className="h-3 w-px bg-gray-300" />
                    <div className="text-body-xl text-gray-400">
                      {value.email}
                    </div>
                  </VFlex>
                ),
                role: value => startCase(mapUserRole(value)),
                remove: (_1, _2, row) => {
                  const disabled =
                    row.role === USER_CONST.USER_ROLE.SITE_ADMIN ||
                    row.role === USER_CONST.USER_ROLE.ADMIN ||
                    row.role === USER_CONST.USER_ROLE.GUEST;
                  return (
                    <VFlex justify="center" items="center" className="w-full">
                      <VIconButton
                        Icon={Remove}
                        size="md"
                        variant="roundDanger"
                        disabled={disabled}
                        onClick={e => {
                          e.stopPropagation();
                          onUserRemoveButtonClick(row.id);
                        }}
                      />
                    </VFlex>
                  );
                },
                lastWorked: () => '-',
              }}
              columnFilter={
                columnFilter as VTableColumnFilter<
                  (typeof renderedUserList)[number]
                >[]
              }
              onColumnFilterChange={e => {
                const roleColumnFilter = e.find(filter => filter.id === 'role');
                const roleColumnFilterItemList =
                  roleColumnFilter?.itemList.filter(item => item.isSelected);
                setFilteredUserRoleList(
                  roleColumnFilterItemList?.map(item => item.value) ?? [],
                );
              }}
            />
          </div>

          {selectedGroup.id === 0 && (
            <VFlex justify="center" items="center">
              <VPagination
                currentPage={offset / limit + 1}
                totalPage={Math.ceil((totalUserCount ?? 0) / limit)}
                onChange={page => setOffset((page - 1) * limit)}
              />
            </VFlex>
          )}
        </VStack>
      </VStack>
    </VFlex>
  );
};

UserGroupList.displayName = 'UserGroupList';

export default UserGroupList;
